import Vue from 'vue'

import Cookies from 'js-cookie'
import Element from 'element-ui'
import lang from 'element-ui/lib/locale/lang/en'
import locale from 'element-ui/lib/locale'
import { VueReCaptcha } from 'vue-recaptcha-v3'

// Import the config first to init the config instance & assign config to service-locator
import './config'
import './styles/element-variables.scss'

import '@/styles/index.scss' // global css

import VueApexCharts from 'vue-apexcharts'
import FlagIcon from 'vue-flag-icon'

import App from './App'
import { buildRootStore } from './store'
import router from './router'
import i18n from './i18n'

import './assets/icons' // icon
import './permission' // permission control
import './utils/error-log' // error log

import * as filters from './filters'

import locator from '@/service-locator'
import ClientLogger from '@/plugins/logger'
import AbTestProvider from '@/plugins/abt'
import { initializeStatsigWithCountry } from '@/plugins/abt/initializer'
import { buildChatBotService } from '@/plugins/chatbot/handler'
import { buildProductTourService } from '@/plugins/product-tour/guidesail'
import { buildTrackingPlugin } from '@/plugins/tracking/plugin'
import { detectCurrentLocale } from '@/utils/i18n'
import { applyApexChartDefaults } from '@/utils/apexcharts-config'

const setupApp = async () => {
  Vue.use(Element, {
    size: Cookies.get('size') || 'medium', // set element-ui default size
    i18n: (key, value) => i18n.t(key, value),
  })

  applyApexChartDefaults(detectCurrentLocale())

  Vue.use(VueApexCharts)
  Vue.use(AbTestProvider)
  Vue.component('apexchart', VueApexCharts)
  Vue.use(FlagIcon)

  // configure language for Element-UI components
  locale.use(lang)

  // Enable Google reCAPTCHA
  if (process.env.VUE_APP_RECAPTCHA_SITEKEY) {
    // Config Google's reCAPTCHA v3
    Vue.use(VueReCaptcha, {
      siteKey: process.env.VUE_APP_RECAPTCHA_SITEKEY,
      autoHideBadge: true,
    })
  }

  // Initialize Statsig with country detection
  await initializeStatsigWithCountry()

  // register global utility filters
  Object.keys(filters).forEach((key) => {
    Vue.filter(key, filters[key])
  })

  Vue.config.productionTip = false

  buildRootStore(locator)

  // Init chatbot service
  buildChatBotService(locator)

  buildProductTourService(locator)

  buildTrackingPlugin(locator)

  // Catch error for Newrelic
  Vue.config.errorHandler = (err, vm, info) => {
    if (window.newrelic) {
      const component = vm?.$options?.name || 'Anonymous'

      window.newrelic.setCustomAttribute('component', component)
      window.newrelic.setCustomAttribute('info', info)

      window.newrelic.noticeError(err)
    }
  }

  const version = locator.config.env + '.' + locator.config.releaseVersion

  Vue.use(ClientLogger, { version })

  // eslint-disable-next-line no-new
  new Vue({
    el: '#app',
    router,
    store: locator.store,
    i18n,
    created: () => {
      document.documentElement.setAttribute(
        'dir',
        locator.store.getters['translations/currentDirection']
      )
    },
    render: (h) => h(App),
  })

  window.__version__ = version
}

setupApp()
